/* eslint-disable indent */
import React from "react";
import { parseLocalYMD } from "../../../utils/fechasFuturosPanel";
import styles from "../stylesERv2.module.scss";

// Fix H7 (bug de huso horario): new Date("YYYY-MM-DD") parsea como UTC medianoche,
// y en husos con offset negativo (Chile/Perú) el día 1 de cada mes cae en el mes anterior.
const fechaLocalDeNegocio = (fechaStr) => parseLocalYMD(fechaStr) || new Date(NaN);

export const CostosFijosTable = ({
  modoCombinado,
  costosFijosCollapsed,
  setCostosFijosCollapsed,
  costosFijosMensualesCombinados,
  monthsToShow,
  sumarCostosFijosMes,
  formatCurrency,
  porcentajeSobreFacturacion,
  costosFijosYTD,
  rrhhCollapsed,
  setRrhhCollapsed,
  isEmpleadosRRHHLoading,
  rrhhMensualConBeneficios,
  empleadosRRHH,
  getSueldoBaseVigente,
  selectedYear,
  pais,
  puedeEditar,
  nombresCostosFijos,
  camposCostosFijos,
  meses,
  rawCostosFijos,
  costosFijosData,
  getProjectedCostoFijo,
  verEnDolares,
  dolarMensual,
  localString,
  openEditModal,
  onDetalleFactura,
  verUltimos12Meses,
  costosFijosMensualWindowed,
  // FASE E2c: una ventana por cuenta (`enVentana(costosFijosPorConcepto[idx], ...AnoAnterior)`)
  // — el índice `i` de cada celda, en LTM, es un SLOT de ventana, no un mes calendario; antes
  // esta tabla leía `costosFijosData[i]?.[campo]` crudo (el mes `i` del año EN CURSO) incluso
  // con LTM prendido, mostrando el mes equivocado del año en vez del mes real de la ventana.
  costosFijosPorConceptoWindowed = null,
  // FASE E2b: fuente única Chile+Perú (`construirFilasER`) para RRHH y las 13 cuentas en
  // modo combinado — antes esta sección entera se ocultaba (`!modoCombinado`), mostrando
  // SOLO el total y nunca el detalle (hallazgo E1 §3a).
  filasCombinado = null,
  // FASE 3 PASO 4 (criterio 1), punto 1a — RRHH (Chile): bottom-up Criterio 1b, ya ventaneado
  // para LTM (`vUSDChile`, index.js). `dolarMensualPromedioChile` alimenta el USD de las filas
  // de detalle (empleado por empleado, Gratificaciones) — NINGUNA de las dos tiene fecha propia
  // (decisión medición previa punto 4: nunca `fecha_pago`), así que van siempre con TC promedio.
  vRRHHUSD = null,
  // "Costos Fijos" (total): bottom-up, SOLO sumando filas ya convertidas (`construirFilasER`) —
  // nunca una reconversión genérica del total en pesos (si no, deja de cuadrar con RRHH apenas
  // esa fila entre en Criterio 1; 10.1, "no quedan dos caminos").
  vCostosFijosUSD = null,
  // FASE 3 PASO 4 (criterio 1), punto 1b — las 13 cuentas de Costos Fijos (Oficina...Servicios
  // Online), bottom-up por cuenta: 2026+ cada factura con el TC de su propia fecha_emision
  // (Criterio 1a), <2026 (manual) TC promedio del mes (Criterio 1b) — mismo `vUSDChile` que
  // RRHH, ya ventaneado para LTM. `{ [nombreCuenta]: Array(12) }`.
  vCuentasFijasUSD = null,
  dolarMensualPromedioChile = null,
}) => {
  const yaEnUSDChile = pais === "Chile" && verEnDolares;
  // US$ de una celda SIN fecha propia (Criterio 1b): TC promedio del mes `i`, nunca el TC de
  // `formatCurrency` (ese es el criterio de hoy, un solo TC por mes — 10.1, un solo camino).
  const celdaPromedio = (valorPeso, i) => {
    if (!yaEnUSDChile) return formatCurrency(valorPeso, i);
    const tc = dolarMensualPromedioChile?.[i] || 0;
    return formatCurrency(tc > 0 ? valorPeso / tc : 0, i, null, true);
  };
  // `serieFallback` preserva EXACTO el array que ya recibía `formatCurrency` antes de este
  // punto (p.ej. sueldo sin egreso en las filas de empleado) — evita que Perú (que sigue
  // cayendo acá, `!yaEnUSDChile`) cambie de número por el refactor. `serieCriterio1b` (default
  // = `serieFallback`) es la que realmente se divide por el TC promedio para Chile.
  const celdaPromedioYTD = (sumaPeso, serieCriterio1b, serieFallback = serieCriterio1b) => {
    if (!yaEnUSDChile) return formatCurrency(sumaPeso, null, serieFallback);
    const sumaUsd = (serieCriterio1b || []).slice(0, monthsToShow).reduce((acc, v, i) => {
      const tc = dolarMensualPromedioChile?.[i] || 0;
      return acc + (tc > 0 ? (v || 0) / tc : 0);
    }, 0);
    return formatCurrency(sumaUsd, null, null, true);
  };
  return (
    <>
      {/* === SECCIÓN COSTOS FIJOS === */}
      <tr
        className={styles.totalCostosRow}
        onClick={() => setCostosFijosCollapsed(!costosFijosCollapsed)}
        style={{ cursor: "pointer", userSelect: "none" }}
      >
        <td className={styles.conceptCell}>Costos Fijos {!verUltimos12Meses && costosFijosCollapsed ? ">" : ""}</td>
        {Array(12)
          .fill(0)
          .map((_, i) => {
            if (i >= monthsToShow) return <td key={i}>-</td>;

            // FASE 3 PASO 4 (criterio 1), punto 3 — Sumar Perú: `filasCombinado["Costos Fijos"]`
            // (construirFilasER, SOLO sumando RRHH+13cuentas ya convertidas) en vez de
            // `costosFijosMensualesCombinados` (un total aparte, recalculado con el criterio de
            // hoy) — si no, el total deja de cuadrar con RRHH/cuentas apenas esas filas entran
            // en Criterio 1 (10.1, "no quedan dos caminos").
            if (modoCombinado) {
              return (
                <td key={i} className="text-end font-weight-bold">
                  {formatCurrency(filasCombinado["Costos Fijos"].usd[i], null, null, true)}
                </td>
              );
            }
            if (yaEnUSDChile) {
              return (
                <td key={i} className="text-end font-weight-bold">
                  {formatCurrency(vCostosFijosUSD?.[i] || 0, null, null, true)}
                </td>
              );
            }
            const totalMes = verUltimos12Meses ? costosFijosMensualWindowed[i] || 0 : sumarCostosFijosMes(i);
            return (
              <td key={i} className="text-end font-weight-bold">
                {formatCurrency(totalMes, i)}
              </td>
            );
          })}
        <td className={styles.totalCell}>
          {modoCombinado
            ? formatCurrency(
                filasCombinado["Costos Fijos"].usd.slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0),
                null,
                null,
                true
              )
            : yaEnUSDChile
              ? formatCurrency(
                  (vCostosFijosUSD || []).slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0),
                  null,
                  null,
                  true
                )
              : formatCurrency(
                  costosFijosYTD,
                  null,
                  verUltimos12Meses
                    ? costosFijosMensualWindowed
                    : Array.from({ length: 12 }, (_, i) => (i < monthsToShow ? sumarCostosFijosMes(i) : 0))
                )}{" "}
          |{" "}
          {porcentajeSobreFacturacion(
            modoCombinado
              ? costosFijosMensualesCombinados.slice(0, monthsToShow).reduce((a, b) => a + b, 0)
              : costosFijosYTD
          )}
        </td>
      </tr>

      {!costosFijosCollapsed && (
        <>
          {/* NUEVA SECCIÓN RRHH */}
          <tr
            onClick={() => {
              if (!modoCombinado) setRrhhCollapsed(!rrhhCollapsed);
            }}
            style={{ cursor: modoCombinado ? "default" : "pointer", userSelect: "none" }}
          >
            <td className={styles.conceptCell}>
              RRHH {!modoCombinado && (rrhhCollapsed ? ">" : "∨")}
              {isEmpleadosRRHHLoading && " (cargando...)"}
            </td>
            {Array(12)
              .fill(0)
              .map((_, i) => (
                <td key={i} className="text-end font-weight-bold">
                  {i < monthsToShow
                    ? modoCombinado
                      ? formatCurrency(filasCombinado.RRHH.usd[i], null, null, true)
                      : yaEnUSDChile
                        ? formatCurrency(vRRHHUSD?.[i] || 0, null, null, true)
                        : formatCurrency(rrhhMensualConBeneficios[i], i)
                    : "-"}
                </td>
              ))}
            <td className={styles.totalCell}>
              {modoCombinado ? (
                (() => {
                  const totalComb = filasCombinado.RRHH.usd.slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0);
                  return (
                    <>
                      {formatCurrency(totalComb, null, null, true)} | {porcentajeSobreFacturacion(totalComb)}
                    </>
                  );
                })()
              ) : yaEnUSDChile ? (
                (() => {
                  const totalUSD = (vRRHHUSD || []).slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0);
                  const totalPeso = rrhhMensualConBeneficios.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
                  return (
                    <>
                      {formatCurrency(totalUSD, null, null, true)} | {porcentajeSobreFacturacion(totalPeso)}
                    </>
                  );
                })()
              ) : (
                <>
                  {formatCurrency(
                    rrhhMensualConBeneficios.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
                    null,
                    rrhhMensualConBeneficios
                  )}{" "}
                  |{" "}
                  {porcentajeSobreFacturacion(
                    rrhhMensualConBeneficios.slice(0, monthsToShow).reduce((a, b) => a + b, 0)
                  )}
                </>
              )}
            </td>
          </tr>
          {!modoCombinado && !rrhhCollapsed && (
            <>
              {empleadosRRHH.map((emp) => {
                const tieneSueldoEnYTD = Array(monthsToShow)
                  .fill(0)
                  .some((_, i) => getSueldoBaseVigente(emp, i) > 0);
                // También mostrar si tiene egreso en el año (aunque no tenga sueldo)
                const tieneEgresoEnYTD =
                  emp.egreso_fecha &&
                  emp.egreso_monto &&
                  fechaLocalDeNegocio(emp.egreso_fecha).getFullYear() === selectedYear &&
                  fechaLocalDeNegocio(emp.egreso_fecha).getMonth() < monthsToShow;
                if (!tieneSueldoEnYTD && !tieneEgresoEnYTD) return null;

                return (
                  <tr key={emp.id} className={styles.rrhhSubrow}>
                    <td className={styles.conceptCell} style={{ paddingLeft: "30px" }}>
                      {emp.nombre} {emp.apellido}
                    </td>
                    {Array(12)
                      .fill(0)
                      .map((_, i) => {
                        const valor = i < monthsToShow ? getSueldoBaseVigente(emp, i) : 0;
                        const tieneEgreso =
                          emp.egreso_fecha &&
                          emp.egreso_monto &&
                          fechaLocalDeNegocio(emp.egreso_fecha).getFullYear() === selectedYear &&
                          fechaLocalDeNegocio(emp.egreso_fecha).getMonth() === i;
                        return (
                          <td key={i} className="text-end">
                            {valor > 0 && celdaPromedio(valor, i)}
                            {valor === 0 && !tieneEgreso && "-"}
                            {tieneEgreso && (
                              <div style={{ color: "#dc2626", fontWeight: "600" }}>
                                {celdaPromedio(Number(emp.egreso_monto), i)}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    <td className={styles.totalCell}>
                      {(() => {
                        const serieCompleta = Array.from({ length: 12 }, (_, i) => {
                          let valor = getSueldoBaseVigente(emp, i);
                          if (emp.egreso_monto && emp.egreso_fecha) {
                            const fechaEgreso = fechaLocalDeNegocio(emp.egreso_fecha);
                            if (fechaEgreso.getFullYear() === selectedYear && fechaEgreso.getMonth() === i) {
                              valor += Number(emp.egreso_monto) || 0;
                            }
                          }
                          return valor;
                        });
                        const sumaPeso = serieCompleta.slice(0, monthsToShow).reduce((a, b) => a + b, 0);
                        // Fallback EXACTO al de antes de este punto (sueldo sin egreso) — nunca
                        // cambia el número de Perú, que sigue cayendo en esa rama.
                        const serieSueldoSolo = Array.from({ length: 12 }, (_, i) => getSueldoBaseVigente(emp, i));
                        return celdaPromedioYTD(sumaPeso, serieCompleta, serieSueldoSolo);
                      })()}
                    </td>
                  </tr>
                );
              })}
              {/* CTS y Gratificaciones */}
              {pais === "Perú" && (
                <tr className={styles.rrhhSubrow}>
                  <td className={styles.conceptCell} style={{ paddingLeft: "30px", fontStyle: "italic" }}>
                    CTS y Gratificaciones
                  </td>
                  {Array(12)
                    .fill(0)
                    .map((_, i) => {
                      // Solo mostrar meses transcurridos, no futuros
                      if (i >= monthsToShow) {
                        return (
                          <td key={i} className="text-end">
                            -
                          </td>
                        );
                      }
                      const mes = i + 1;
                      let valor = 0;

                      // Gratificación de julio (paga en junio)
                      if (mes === 6) {
                        valor += empleadosRRHH.reduce((sum, emp) => {
                          const sueldo = getSueldoBaseVigente(emp, 5);
                          return sum + (sueldo > 0 ? sueldo : 0);
                        }, 0);
                      }

                      // Gratificación de diciembre (paga en diciembre)
                      if (mes === 12) {
                        valor += empleadosRRHH.reduce((sum, emp) => {
                          const sueldo = getSueldoBaseVigente(emp, 11);
                          return sum + (sueldo > 0 ? sueldo : 0);
                        }, 0);
                      }

                      // CTS mayo (por semestre nov-abr)
                      if (mes === 5) {
                        valor += empleadosRRHH.reduce((sum, emp) => {
                          const sueldo = getSueldoBaseVigente(emp, 4);
                          return sum + (sueldo > 0 ? sueldo : 0);
                        }, 0);
                      }

                      // CTS noviembre (por semestre may-oct)
                      if (mes === 11) {
                        valor += empleadosRRHH.reduce((sum, emp) => {
                          const sueldo = getSueldoBaseVigente(emp, 10);
                          return sum + (sueldo > 0 ? sueldo : 0);
                        }, 0);
                      }

                      return (
                        <td key={i} className="text-end">
                          {valor > 0 ? formatCurrency(valor, i) : "-"}
                        </td>
                      );
                    })}
                  <td className={styles.totalCell}>
                    {formatCurrency(
                      Array(monthsToShow)
                        .fill(0)
                        .reduce((sum, _, i) => {
                          const mes = i + 1;
                          let valor = 0;
                          if (mes === 5 || mes === 6 || mes === 11 || mes === 12) {
                            valor = empleadosRRHH.reduce((s, emp) => {
                              const sueldo = getSueldoBaseVigente(emp, i);
                              return s + (sueldo > 0 ? sueldo : 0);
                            }, 0);
                          }
                          return sum + valor;
                        }, 0),
                      null,
                      Array.from({ length: 12 }, (_, i) => {
                        const mes = i + 1;
                        if (mes === 5 || mes === 6 || mes === 11 || mes === 12) {
                          return empleadosRRHH.reduce((s, emp) => {
                            const sueldo = getSueldoBaseVigente(emp, i);
                            return s + (sueldo > 0 ? sueldo : 0);
                          }, 0);
                        }
                        return 0;
                      })
                    )}
                  </td>
                </tr>
              )}
              {/* Gratificaciones Chile */}
              {pais === "Chile" && (
                <tr className={styles.rrhhSubrow}>
                  <td className={styles.conceptCell} style={{ paddingLeft: "30px", fontStyle: "italic" }}>
                    Gratificaciones
                  </td>
                  {(() => {
                    // Gratificación Chile: IMM=500000, tope 4.75*IMM/12 — serie completa (12 meses),
                    // reusada por las celdas y el total (nunca dos cálculos del mismo número).
                    const IMM = 500000;
                    const gratMax = (4.75 * IMM) / 12;
                    const serieGrat = Array.from({ length: 12 }, (_, i) =>
                      empleadosRRHH.reduce((sum, emp) => {
                        const sueldo = getSueldoBaseVigente(emp, i);
                        if (sueldo > 0) return sum + Math.round(Math.min((sueldo * 0.25) / 12, gratMax));
                        return sum;
                      }, 0)
                    );
                    return (
                      <>
                        {serieGrat.map((valor, i) => (
                          <td key={i} className="text-end">
                            {i >= monthsToShow ? "-" : valor > 0 ? celdaPromedio(valor, i) : "-"}
                          </td>
                        ))}
                        <td className={styles.totalCell}>
                          {celdaPromedioYTD(
                            serieGrat.slice(0, monthsToShow).reduce((a, b) => a + b, 0),
                            serieGrat
                          )}
                        </td>
                      </>
                    );
                  })()}
                </tr>
              )}
            </>
          )}
          {nombresCostosFijos.map((concepto, index) => {
            if (concepto === "RRHH") return null;
            const campo = camposCostosFijos[index];
            // FASE 3 PASO 4 (criterio 1), punto 1b — las 13 cuentas (incluida Asesoría externa,
            // ajuste de Cowork: NO reactiva su código muerto propio, pasa por el MISMO camino
            // que las otras 12) leen `vCuentasFijasUSD[concepto]` en Chile+USD — ya ventaneado
            // para LTM (`vUSDChile`, index.js), 10.1 "un solo camino".
            const usdCuentaChile = vCuentasFijasUSD?.[concepto] || null;
            // Compat Perú (NO forma parte del Criterio 1, que es Chile-solo — 10.265/10.267):
            // Asesoría externa era la ÚNICA cuenta con un caso especial propio en USD, que
            // formateaba con `localString(pais)` ("en-US" para Perú) en vez de con el locale
            // genérico de `formatCurrency` ("es-CL" siempre que verEnDolares). Al retirar ese
            // caso especial (ajuste de Cowork, Chile), Perú quedaba con el MISMO número pero
            // OTRO separador de miles ("1,483" -> "1.483") — un diff de Perú que esta fase no
            // puede producir. Se preserva BYTE A BYTE con este formateador, acotado a esta
            // cuenta+país; no es el caso especial que se retiró (ese mezclaba USD guardado y
            // fecha real, nunca poblados) — es solo el locale de salida.
            const esAsesoriaExternaPeru = campo === "asesoria_externa" && pais === "Perú";
            const formatUSDPeruLegacy = (valorUSD) =>
              `US$ ${Math.round(valorUSD).toLocaleString(localString(pais), {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`;

            return (
              <tr key={concepto}>
                <td className={styles.conceptCell}>{concepto}</td>
                {meses.map((_, i) => {
                  // FASE E2b: modo combinado (Sumar Perú) lee de `filasCombinado` (fuente única),
                  // celda de solo lectura — en modo normal la celda sigue siendo editable.
                  if (modoCombinado) {
                    return (
                      <td key={i}>
                        {i < monthsToShow ? formatCurrency(filasCombinado[concepto].usd[i], null, null, true) : "-"}
                      </td>
                    );
                  }
                  if (i >= monthsToShow) return <td key={i}>-</td>;
                  if (yaEnUSDChile) {
                    return <td key={i}>{formatCurrency(usdCuentaChile?.[i] || 0, null, null, true)}</td>;
                  }
                  if (esAsesoriaExternaPeru && verEnDolares && !verUltimos12Meses) {
                    const valorLocal = costosFijosData[i]?.[campo] ?? 0;
                    const valorUSD = valorLocal > 0 && dolarMensual[i] ? valorLocal / dolarMensual[i] : 0;
                    return <td key={i}>{formatUSDPeruLegacy(valorUSD)}</td>;
                  }
                  // FASE E2c: LTM lee la ventana por cuenta (`i` es un slot de ventana, no un mes
                  // calendario del año en curso) — celda de solo lectura.
                  if (verUltimos12Meses) {
                    const valorVentana = costosFijosPorConceptoWindowed?.[index]?.[i] ?? 0;
                    return <td key={i}>{formatCurrency(valorVentana, i)}</td>;
                  }
                  const registroExiste = rawCostosFijos?.some(
                    (r) => Number(r.mes) === i + 1 && r[campo] !== undefined && r[campo] !== null
                  );
                  const valorReal = costosFijosData[i]?.[campo];
                  const esProyectado = !registroExiste;
                  const valor = esProyectado ? getProjectedCostoFijo(campo, i) : (valorReal ?? 0);

                  return (
                    <td key={i}>
                      <div
                        onClick={() => {
                          if (puedeEditar) openEditModal(concepto, i, campo);
                          // v2: la celda abre el detalle de las facturas que la componen.
                          if (onDetalleFactura?.disponible(i)) onDetalleFactura.abrir(campo, concepto, i);
                        }}
                        style={{
                          cursor: puedeEditar || onDetalleFactura?.disponible(i) ? "pointer" : "default",
                        }}
                      >
                        {formatCurrency(valor, i)}
                      </div>
                    </td>
                  );
                })}
                <td className={styles.totalCell}>
                  {modoCombinado
                    ? formatCurrency(
                        filasCombinado[concepto].usd.slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0),
                        null,
                        null,
                        true
                      )
                    : yaEnUSDChile
                      ? formatCurrency(
                          (usdCuentaChile || []).slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0),
                          null,
                          null,
                          true
                        )
                      : verUltimos12Meses
                        ? (() => {
                            const serieVentana = costosFijosPorConceptoWindowed?.[index] || Array(12).fill(0);
                            const totalVentana = serieVentana.slice(0, monthsToShow).reduce((a, b) => a + (b || 0), 0);
                            return formatCurrency(totalVentana, null, serieVentana);
                          })()
                        : esAsesoriaExternaPeru && verEnDolares
                          ? (() => {
                              let totalUsd = 0;
                              for (let i = 0; i < monthsToShow; i++) {
                                const valorLocal = costosFijosData[i]?.[campo] || 0;
                                if (valorLocal > 0 && dolarMensual[i]) totalUsd += valorLocal / dolarMensual[i];
                              }
                              return formatUSDPeruLegacy(totalUsd);
                            })()
                          : formatCurrency(
                              costosFijosData.slice(0, monthsToShow).reduce((sum, row) => sum + (row?.[campo] || 0), 0),
                              null,
                              costosFijosData.map((row) => row?.[campo] || 0)
                            )}
                </td>
              </tr>
            );
          })}
        </>
      )}
    </>
  );
};
